import * as pdfjs from 'pdfjs-dist';

// Configure pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface ExtractedArrearData {
  profile: {
    name: string;
    pran: string;
    pan: string;
    designation: string;
    classRange: string;
    office: string;
    department: string;
    district: string;
  };
  rawPage2Text?: string;
  monthlyDrawn: {
    month: string;
    pay: number;
    da: number;
    hra: number;
    ma: number;
    cta: number;
    nps: number;
    gis: number;
    ptax: number;
    itax: number;
  }[];
}

export const extractArrearData = async (file: File, isTds: boolean = false, joiningDate?: string, financialYear: string = '2025-26'): Promise<ExtractedArrearData> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  
  // Rule: Extract data ONLY from Page 2
  if (pdf.numPages < 2) {
    throw new Error("PDF must have at least 2 pages. Data extraction is restricted to Page 2.");
  }
  
  const page = await pdf.getPage(2);
  const textContent = await page.getTextContent();
  
  // Sort items to maintain reading order
  const items = [...textContent.items] as any[];
  items.sort((a, b) => {
    const yDiff = b.transform[5] - a.transform[5];
    if (Math.abs(yDiff) < 5) return a.transform[4] - b.transform[4];
    return yDiff;
  });
  
  const text = items.map(item => item.str).join(" ");

  // 1. Employee Profile Mapping
  // Rule: Name is between 'Name' or 'Employee Name' and 'Current'
  const nameMatch = text.match(/(?:Name|Employee Name|EMPLOYEE NAME)\s*[:\-]?\s*(.*?)(?=\s*(?:Current|Designation|PAN|PRAN|$))/i);
  const rawName = nameMatch ? nameMatch[1].trim() : "NOT FOUND";
  const formattedName = rawName;

  // Rule: PRAN NO is 12 digits after 'GPF/PRAN NO'
  let formattedPran = "NOT FOUND";
  if (!isTds) {
    const pranMatch = text.match(/(?:GPF\/\s*PRAN NO)\s*[:\-]?\s*(\d{12})/i);
    const rawPran = pranMatch ? pranMatch[1] : "NOT FOUND";
    formattedPran = `PRAN NO :- ${rawPran}`;
  }

  // Rule: PAN NO extraction
  let rawPan = "NOT FOUND";
  if (isTds) {
    // Specific logic for TDS: Between 'PAN No :' and 'Bill Type'
    const panBoundaryMatch = text.match(/PAN\s+No\s*:\s*(.*?)\s*Bill\s+Type/i);
    if (panBoundaryMatch) {
      rawPan = panBoundaryMatch[1].trim();
    } else {
      // Fallback: Standard PAN regex
      const panMatch = text.match(/(?:PAN|PAN NO)\s*[:\-]?\s*([A-Z]{5}\d{4}[A-Z])/i);
      rawPan = panMatch ? panMatch[1] : "NOT FOUND";
    }
  } else {
    // Standard logic for Arrear
    const panMatch = text.match(/(?:PAN|PAN NO)\s*[:\-]?\s*([A-Z]{5}\d{4}[A-Z])/i);
    rawPan = panMatch ? panMatch[1] : "NOT FOUND";
  }

  // Office Name
  const officeMatch = text.match(/(?:Current Office|CURRENT OFFICE)\s*[:\-]?\s*(.*?)(?=\s*(?:Designation|DESIGNATION|Employee Name|EMPLOYEE NAME|GPF\/PRAN|PRAN|PAN NO|CLASS|Department|DEPARTMENT|$))/i);
  const office = officeMatch ? officeMatch[1].trim() : "NOT FOUND";

  // Department
  let department = "NOT FOUND";
  if (!isTds) {
    const deptMatch = text.match(/(?:Current Department|CURRENT DEPARTMENT)\s*[:\-]?\s*(.*?)(?=\s*(?:Designation|DESIGNATION|Employee Name|EMPLOYEE NAME|GPF\/PRAN|PRAN|PAN NO|CLASS|Office|OFFICE|$))/i);
    department = deptMatch ? deptMatch[1].trim().replace(/\(.*\)/, '').trim() : "NOT FOUND";
  }

  // District (Extracted from Office)
  let district = "NOT FOUND";
  if (isTds) {
    // Specific logic for TDS: Between "Current Office : District Education Office " and " ("
    const districtBoundaryMatch = text.match(/Current\s+Office\s*:\s*District\s+Education\s+Office\s+(.*?)\s+\(/i);
    if (districtBoundaryMatch) {
      district = districtBoundaryMatch[1].trim().toUpperCase();
    } else if (office !== "NOT FOUND") {
      const districtMatch = office.match(/\s+([A-Za-z]+)(?:\s+\(\d+\))?\s*$/);
      if (districtMatch) district = districtMatch[1].trim().toUpperCase();
    }
  } else {
    // Standard logic for Arrear
    if (office !== "NOT FOUND") {
      const districtMatch = office.match(/\s+([A-Za-z]+)(?:\s+\(\d+\))?\s*$/);
      if (districtMatch) {
        district = districtMatch[1].trim();
      } else {
        // Fallback: search for common Bihar districts if the regex fails
        const commonDistricts = ["Gopalganj", "Patna", "Muzaffarpur", "Bhagalpur", "Siwan", "Saran", "Vaishali"];
        for (const d of commonDistricts) {
          if (office.includes(d)) {
            district = d;
            break;
          }
        }
      }
    }
  }

  // Designation & Class
  let rawDesig = "SCHOOL TEACHER";
  if (isTds) {
    // Specific logic for TDS: Between "Designation : " and " Employee"
    const desigBoundaryMatch = text.match(/Designation\s*:\s*(.*?)\s*Employee/i);
    if (desigBoundaryMatch) {
      rawDesig = desigBoundaryMatch[1].trim();
    } else {
      const desigMatch = text.match(/(?:Designation|DESIGNATION)\s*[:\-]?\s*(.*?)(?=\s*(?:Employee Name|EMPLOYEE NAME|Current Office|CURRENT OFFICE|GPF\/PRAN|PRAN|PAN NO|CLASS|$))/i);
      rawDesig = desigMatch ? desigMatch[1].trim() : "SCHOOL TEACHER";
    }
  } else {
    const desigMatch = text.match(/(?:Designation|DESIGNATION)\s*[:\-]?\s*(.*?)(?=\s*(?:Employee Name|EMPLOYEE NAME|Current Office|CURRENT OFFICE|GPF\/PRAN|PRAN|PAN NO|CLASS|$))/i);
    rawDesig = desigMatch ? desigMatch[1].trim() : "SCHOOL TEACHER";
  }
  
  // Clean up designation if it leaked
  if (/Employee Name/i.test(rawDesig)) rawDesig = rawDesig.split(/Employee Name/i)[0].trim().replace(/[:\-]+$/, '').trim();
  if (/Current Office/i.test(rawDesig)) rawDesig = rawDesig.split(/Current Office/i)[0].trim().replace(/[:\-]+$/, '').trim();
  if (/PRAN/i.test(rawDesig)) rawDesig = rawDesig.split(/PRAN/i)[0].trim().replace(/[:\-]+$/, '').trim();
  
  let treSuffix = "";
  if (text.includes("TRE-1")) treSuffix = "BPSC TRE-1 ";
  else if (text.includes("TRE-2")) treSuffix = "BPSC TRE-2 ";
  else if (text.includes("TRE-3")) treSuffix = "BPSC TRE-3 ";
  
  const designation = `${treSuffix}${rawDesig.toUpperCase()}`;

  // Extract bracketed class (e.g., 1-5)
  const classMatch = text.match(/\((\d+-\d+)\)/);
  const rawClass = classMatch ? classMatch[1] : "NOT FOUND";
  const formattedClass = `CLASS :- ${rawClass}`;

  // 2. Monthly Table Extraction (March to February)
  const months = [
    "March", "April", "May", "June", "July", "August", 
    "September", "October", "November", "December", "January", "February"
  ];

  // Helper to extract numbers after a specific label in a specific section
  const extractNumbersForLabel = (label: string, source: string, nextLabels: string[] = []): number[] => {
    if (isTds && nextLabels.length > 0) {
      // Try to find a segment between this label and any of the next labels
      for (const nextLabel of nextLabels) {
        // Escape special characters and handle multiple spaces in labels
        const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        const escapedNextLabel = nextLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        
        // Use a more flexible regex for spaces between labels and content
        const regex = new RegExp(`${escapedLabel}\\s*(.*?)\\s*${escapedNextLabel}`, 'i');
        const match = source.match(regex);
        if (match) {
          const segmentText = match[1];
          const numbers = segmentText.match(/\d[\d,.]*/g) || [];
          const result = numbers
            .map(v => v.replace(/,/g, ''))
            .filter(v => v.length > 0 && !isNaN(parseFloat(v)))
            .map(v => parseFloat(v));
          
          // If we found at least some numbers, we assume this is the correct segment
          if (result.length > 0) {
            return result.slice(0, 12); // Take only first 12 months
          }
        }
      }
    }

    const regex = new RegExp(`${label}[^\\d]*?([\\d\\s,.]+)`, 'i');
    const match = source.match(regex);
    if (!match) return [];
    
    const numberStrings = match[1].match(/\d[\d,.]*/g) || [];
    return numberStrings
      .map(v => v.replace(/,/g, ''))
      .filter(v => v.length > 0 && !isNaN(parseFloat(v)))
      .map(v => parseFloat(v));
  };

  // Rule: Search ONLY for "Dearness Allowance"
  let basicPays = extractNumbersForLabel("Basic Pay", text, ["House Rent", "Dearness", "DA"]);
  if (basicPays.length === 0) basicPays = extractNumbersForLabel("PAY", text, ["House Rent", "Dearness", "DA"]);

  let das = extractNumbersForLabel("Dearness", text, ["House Rent", "Medical", "MA"]);
  if (das.length === 0) das = extractNumbersForLabel("Dearness Allowance", text, ["House Rent", "Medical", "MA"]);
  if (das.length === 0) das = extractNumbersForLabel("D\\.A\\.", text, ["House Rent", "Medical", "MA"]);
  if (das.length === 0) das = extractNumbersForLabel("DA", text, ["House Rent", "Medical", "MA"]);

  let hras = extractNumbersForLabel("House Rent Allowance", text, ["Medical", "MA", "Dearness", "DA", "Allowance Dearness"]);
  if (hras.length === 0) hras = extractNumbersForLabel("House Rent", text, ["Allowance Dearness", "Medical", "MA", "Dearness", "DA"]);
  if (hras.length === 0) hras = extractNumbersForLabel("H\\.R\\.A\\.", text, ["Medical", "MA", "Dearness", "DA"]);
  if (hras.length === 0) hras = extractNumbersForLabel("HRA", text, ["Medical", "MA", "Dearness", "DA"]);

  let mas = extractNumbersForLabel("Medical Allowance", text, ["CTA", "GIS"]);
  if (mas.length === 0) mas = extractNumbersForLabel("M\\.A\\.", text, ["CTA", "GIS"]);
  if (mas.length === 0) mas = extractNumbersForLabel("MA", text, ["CTA", "GIS"]);

  let ctas = extractNumbersForLabel("CTA", text, ["GIS", "NPS"]);
  if (ctas.length === 0) ctas = extractNumbersForLabel("C\\.T\\.A\\.", text, ["GIS", "NPS"]);

  let giss = extractNumbersForLabel("GIS", text, ["Professional Tax", "P.TAX", "Income Tax", "I.TAX"]);
  if (giss.length === 0) giss = extractNumbersForLabel("G\\.I\\.S\\.", text, ["Professional Tax", "P.TAX", "Income Tax", "I.TAX"]);
  if (giss.length === 0) giss = extractNumbersForLabel("Group Insurance", text, ["Professional Tax", "P.TAX", "Income Tax", "I.TAX"]);

  let ptaxs = extractNumbersForLabel("Professional Tax", text, ["Income Tax", "I.TAX"]);
  if (ptaxs.length === 0) ptaxs = extractNumbersForLabel("P\\.TAX", text, ["Income Tax", "I.TAX"]);
  if (ptaxs.length === 0) ptaxs = extractNumbersForLabel("P\\. TAX", text, ["Income Tax", "I.TAX"]);

  let itaxs = extractNumbersForLabel("Income Tax", text, ["Total", "Deductions"]);
  if (itaxs.length === 0) itaxs = extractNumbersForLabel("I\\.TAX", text, ["Total", "Deductions"]);
  if (itaxs.length === 0) itaxs = extractNumbersForLabel("I\\. TAX", text, ["Total", "Deductions"]);

  const monthlyDrawn = months.map((month, index) => {
    const rawPay = basicPays[index] || 0;
    const rawDA = das[index] || 0;
    const rawHRA = hras[index] || 0;
    const rawMA = mas[index] || 0;
    const rawCTA = ctas[index] || 0;
    const rawGIS = giss[index] || 0;
    const rawPTAX = ptaxs[index] || 0;
    const rawITAX = itaxs[index] || 0;

    // Calculation & Rounding Logic
    // Basic Pay Rounding: Round to nearest 10 (Dahai)
    const pay = Math.round(rawPay / 10) * 10;
    
    // General Rounding: Standard rounding (no decimals)
    const da = Math.round(rawDA);
    const hra = Math.round(rawHRA);
    
    let ma = Math.round(rawMA);
    if (pay > 0) {
      if (joiningDate) {
        try {
          const jDate = new Date(joiningDate);
          if (!isNaN(jDate.getTime())) {
            const jMonth = jDate.getMonth(); // 0-11
            const jYear = jDate.getFullYear();
            const jDay = jDate.getDate();

            const monthNames = [
              "January", "February", "March", "April", "May", "June", 
              "July", "August", "September", "October", "November", "December"
            ];
            const currentMonthIndex = monthNames.indexOf(month);
            
            // Determine year based on financial year string (e.g., "2025-26")
            const [startYearStr, endYearStr] = financialYear.split('-');
            const startYear = parseInt(startYearStr);
            const endYear = 2000 + parseInt(endYearStr); // Assuming 21st century
            
            // March (2) to Dec (11) are in the start year, Jan (0) and Feb (1) are in the end year.
            const currentYear = (currentMonthIndex >= 2) ? startYear : endYear;

            if (currentYear === jYear && currentMonthIndex === jMonth) {
              // Joining month: (1000 / total_days) * days_worked
              const daysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();
              const daysWorked = daysInMonth - jDay + 1;
              ma = Math.round((1000 / daysInMonth) * daysWorked);
            } else if (currentYear > jYear || (currentYear === jYear && currentMonthIndex > jMonth)) {
              // After joining month
              ma = 1000;
            } else {
              // Before joining month
              ma = 0;
            }
          } else {
            ma = 1000;
          }
        } catch (e) {
          ma = 1000;
        }
      } else if (isTds) {
        // Default for TDS if no joining date provided
        ma = 1000;
      }
    } else {
      // Basic Pay is 0, so MA must be 0
      ma = 0;
    }

    const cta = Math.round(rawCTA);
    const gis = isTds ? (pay > 0 ? 30 : 0) : Math.round(rawGIS);
    const ptax = Math.round(rawPTAX);
    const itax = Math.round(rawITAX);

    // NPS (Col 14): (Difference Pay + Difference DA) × 10%
    // Note: Since this is the "Drawn" section, we calculate NPS on drawn amounts.
    // User formula: (Diff Pay + Diff DA) * 10%. 
    // For drawn section, we'll just show the drawn NPS: (Pay + DA) * 10%
    const nps = Math.round((pay + da) * 0.1);

    return {
      month,
      pay,
      da,
      hra,
      ma,
      cta,
      nps,
      gis,
      ptax: 0, // Initial PTAX to be calculated after all months are processed
      itax
    };
  });

  // Carry forward logic for TDS: if January or February basic pay is 0, use the previous month's data as an estimate
  if (isTds) {
    for (let i = 0; i < monthlyDrawn.length; i++) {
      const m = monthlyDrawn[i];
      if (m.pay === 0 && i > 0 && (m.month === 'January' || m.month === 'February')) {
        const prev = monthlyDrawn[i - 1];
        m.pay = prev.pay;
        m.da = prev.da;
        m.hra = prev.hra;
        m.ma = prev.ma;
        m.cta = prev.cta;
        m.nps = prev.nps;
        m.gis = prev.gis;
        m.itax = prev.itax;
        // ptax is usually 0 for estimated months, so we don't carry it forward
      }
    }
  }

  // Calculate PTAX for TDS based on annual income rules
  if (isTds) {
    const annualIncome = monthlyDrawn.reduce((sum, m) => {
      return sum + (m.pay + m.da + m.hra + m.ma + m.cta);
    }, 0);

    let annualPTAX = 0;
    if (annualIncome > 1000000) {
      annualPTAX = 2500;
    } else if (annualIncome > 500000) {
      annualPTAX = 2000;
    } else if (annualIncome > 300000) {
      annualPTAX = 1000;
    }

    // Apply PTAX only to September
    monthlyDrawn.forEach(m => {
      if (m.month === 'September') {
        m.ptax = annualPTAX;
      } else {
        m.ptax = 0;
      }
    });
  } else {
    // For non-TDS (Arrear), use extracted PTAX
    monthlyDrawn.forEach((m, index) => {
      m.ptax = Math.round(ptaxs[index] || 0);
    });
  }

  return {
    profile: {
      name: formattedName,
      pran: isTds ? "" : formattedPran,
      pan: rawPan,
      designation,
      classRange: formattedClass,
      office: isTds ? "" : office,
      department: isTds ? "" : department,
      district
    },
    rawPage2Text: text,
    monthlyDrawn
  };
}
